/**
 * `expo/fetch`, with the phone saying who it is.
 *
 * Every `/v1` call sends `x-alluzo-client: mobile/<version>/<features>`, so the
 * server can tell this build from an older one still in people's hands and
 * keep answering each the shape it knows (web-redesign-02, Decided 12). The
 * four files that call the API import `fetch` from here instead of from
 * `expo/fetch`; everything else about the request is untouched.
 *
 * The version is `expo.version` in app.json: the one the store build was made
 * from, and the one this JavaScript was bundled with, which is what decides
 * what the app can draw.
 *
 * In the browser this same file runs over `apps/web/src/platform/fetch.ts`,
 * which is where every call ends, and that one names the web instead.
 */
import { CLIENT_HEADER, clientHeaderValue, withClientHeader, type ClientFeature } from '@alluzo/shared';
import { fetch as expoFetch } from 'expo/fetch';

import { expo } from '../../app.json';

/**
 * What this build understands. `roles`: it draws roles, a person's access and
 * Reports to, and OS's role and access cards (web-redesign-04 phase 6), so OS
 * offers it the role tools and kinds rather than the old jobs and levels.
 * `set_view`: the record screen applies a view OS sets (os-live-mode.md 3.1).
 * `team_cards`: it draws OS's phase-3 team cards (roles-parity 3.7), so OS
 * offers it the tools that make them.
 */
const MOBILE_FEATURES: readonly ClientFeature[] = ['roles', 'set_view', 'team_cards'];

export const MOBILE_CLIENT = clientHeaderValue('mobile', expo.version, MOBILE_FEATURES);

/**
 * Wraps one call in a trace span and returns the headers that carry it
 * (`sentry-trace`, `baggage`), so the server continues the phone's trace.
 * `lib/sentry.ts` sets it on the phone: `expo/fetch` is native, and Sentry's
 * own fetch instrumentation never sees it. Unset in the browser, whose Sentry
 * traces the global fetch that `apps/web/src/platform/fetch.ts` calls.
 */
export type FetchTracer = <Result>(
  url: string,
  method: string,
  send: (traceHeaders: { readonly [name: string]: string | undefined }) => Promise<Result>,
) => Promise<Result>;

let tracer: FetchTracer | null = null;

export const setFetchTracer = (next: FetchTracer): void => {
  tracer = next;
};

export const fetch: typeof expoFetch = (input, init) => {
  const sent = withClientHeader(init, MOBILE_CLIENT);
  if (tracer === null) return expoFetch(input, sent);
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  return tracer(url, sent.method ?? 'GET', (traceHeaders) => {
    for (const [name, value] of Object.entries(traceHeaders)) if (value !== undefined) sent.headers.set(name, value);
    return expoFetch(input, sent);
  });
};

/**
 * The headers of a live socket (the setup call, the assistant), with the
 * phone saying who it is as every fetch does. React Native's WebSocket takes
 * headers in its third argument; without this one OS would serve every live
 * call as an old client, whatever this build draws.
 */
export const socketHeaders = (headers: Readonly<Record<string, string>>): Record<string, string> => ({
  ...headers,
  [CLIENT_HEADER]: MOBILE_CLIENT,
});
